/**
 * section-accent — the Founders shell's per-section color vocabulary as an
 * additive contract family (FD1; register FD-CAN-01, ruling D-FD4).
 *
 * The Desk's dock doors, view tabs, and section chrome are color-coded per
 * SECTION. This module owns that vocabulary the way `world-theme.ts` owns
 * the World accent: typed roles, parse/clamp/resolve on the oklch wheel,
 * and a projection to `--ncl-section-*` custom properties. Founders'
 * defaults are the design of record's exact values
 * (`Projects/design_handoff_cockpit_nested_sessions`, hexes converted to
 * oklch) so the shipped palette reproduces the approved design faithfully.
 *
 * Tone-down is a COMPUTED collapse transform (principal words 2026-09-01:
 * "'tone down' … just be using the same colors across groups of things we
 * currently have color coded"): every role takes its GROUP lead's color.
 * The collapsed palette is derived at read time and never stored — there is
 * exactly one persisted vocabulary.
 *
 * Boundary with the sibling accent laws:
 * - Sections mark SURFACES, never speakers. Cleo's reserved hue band
 *   protects speaker identity and binds AGENT accents (TH6's leaf), not
 *   section furniture — the same stance world-theme takes for terra
 *   ("terra marks things, not speakers"). Founders' teal sections sit near
 *   Cleo's hue by the principal's approved design; that collision is
 *   decided, not accidental.
 * - The single-accent tier below "groups" (everything one hue) is a named
 *   later leaf, not built here.
 */

export const SECTION_ACCENT_ROLES = [
  "harmony",
  "rooms",
  "ideas",
  "messages",
  "canvases",
  "workflows",
  "arena",
  "answers",
  "projects",
  "atomBuilder",
  "media",
  "notify",
  "kpi",
  "files",
  "approval",
] as const;

export type SectionAccentRole = (typeof SECTION_ACCENT_ROLES)[number];

export interface SectionAccent {
  readonly hue: number;
  readonly chroma: number;
  readonly lightness: number;
}

export type SectionAccentMap = Readonly<Record<SectionAccentRole, SectionAccent>>;

export const MIN_SECTION_ACCENT_CHROMA = 0.02;
export const MAX_SECTION_ACCENT_CHROMA = 0.24;
export const MIN_SECTION_ACCENT_LIGHTNESS = 0.6;
export const MAX_SECTION_ACCENT_LIGHTNESS = 0.95;

const accent = (lightness: number, chroma: number, hue: number): SectionAccent =>
  Object.freeze({ hue, chroma, lightness });

/**
 * Founders defaults — the design of record's accent hexes, converted to
 * oklch (culori, 3 decimals). Do not tune these by eye; a palette change is
 * a dated design decision.
 *
 * DATED DECISION 2026-09-01 (the principal, on the carved context dock:
 * "can we create a unique color so we dont have matching?"): the design
 * gave Ideas and Files the same #FFCE4D, and the dock shows both doors
 * side by side — hue is identity (FD-CAN-13), so Ideas/Blueprints takes
 * its own blueprint cyan, oklch(0.8 0.13 215) ≈ #37D2F2, the free band
 * between the teal sections (185.5) and KPI blue (244.3). The knowledge
 * group's tone-down lead moves to `files` so the collapsed palette keeps
 * the yellow it had.
 */
export const FOUNDERS_SECTION_ACCENTS: SectionAccentMap = Object.freeze({
  harmony: accent(0.824, 0.167, 157.6), // #4DE59A — Command Central green
  rooms: accent(0.843, 0.137, 185.5), // #3BE8D8 — Molecules teal
  ideas: accent(0.8, 0.13, 215), // #37D2F2 — Blueprints cyan (dated 2026-09-01; see above)
  messages: accent(0.843, 0.137, 185.5), // #3BE8D8 — Messages teal
  canvases: accent(0.686, 0.164, 267.7), // #6E93FF — Whiteboards blue
  workflows: accent(0.73, 0.185, 357.7), // #FF6EA8 — Workflows pink
  arena: accent(0.712, 0.181, 22.8), // #FF6B6B — Arena/Debate red
  // DATED 2026-09-14 (GOALS/GOALS-DESIGN.html#hig5): the two ambers sat 11°
  // from Cleo's new hue 54.5; they move to gold at 80 so no section can wear her.
  answers: accent(0.84, 0.15, 80), // #FDBE45 — Answers gold
  projects: accent(0.84, 0.15, 80), // #FDBE45 — Projects gold
  atomBuilder: accent(0.74, 0.155, 298.3), // #B893FF — Atom Builder purple
  media: accent(0.77, 0.187, 339.2), // #FF7FDB — Image/Video magenta
  notify: accent(0.911, 0.136, 94.1), // #FFE070 — Notify light yellow
  kpi: accent(0.756, 0.135, 244.3), // #5CB8FF — KPI blue
  files: accent(0.872, 0.152, 87.5), // #FFCE4D — Files yellow
  approval: accent(0.672, 0.233, 2.3), // #FF3D8A — approval pink
});

export const SECTION_ACCENT_GROUPS = [
  "work",
  "knowledge",
  "content",
  "comms",
  "attention",
] as const;

export type SectionAccentGroup = (typeof SECTION_ACCENT_GROUPS)[number];

/**
 * Which group each section belongs to, and each group's LEAD — the role
 * whose color the whole group wears when toned down. Grouping is a design
 * decision of this arc; revising membership is a dated palette change.
 */
export const SECTION_ACCENT_GROUP_OF: Readonly<Record<SectionAccentRole, SectionAccentGroup>> =
  Object.freeze({
    harmony: "work",
    rooms: "work",
    workflows: "work",
    projects: "work",
    ideas: "knowledge",
    answers: "knowledge",
    atomBuilder: "knowledge",
    files: "knowledge",
    canvases: "content",
    media: "content",
    kpi: "content",
    messages: "comms",
    notify: "comms",
    arena: "attention",
    approval: "attention",
  });

export const SECTION_ACCENT_GROUP_LEAD: Readonly<Record<SectionAccentGroup, SectionAccentRole>> =
  Object.freeze({
    work: "harmony",
    knowledge: "files", // dated 2026-09-01: was ideas, which left the yellow
    content: "canvases",
    comms: "messages",
    attention: "approval",
  });

function finiteNumber(value: unknown, fallback: number): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function record(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : {};
}

function compactNumber(value: number): string {
  return String(Math.round(value * 100_000) / 100_000);
}

function oklch(lightness: number, chroma: number, hue: number): string {
  return `oklch(${compactNumber(lightness)} ${compactNumber(chroma)} ${compactNumber(hue)})`;
}

/** Wrap any finite hue onto the half-open [0, 360) color wheel. */
export function wrapSectionAccentHue(value: unknown, fallback: number): number {
  const hue = finiteNumber(value, fallback);
  // Round at the projection's own 5-decimal precision so wrapping a value
  // like 517.6 lands exactly on 157.6, not 157.60000000000002.
  return Math.round((((hue % 360) + 360) % 360) * 100_000) / 100_000;
}

export function clampSectionAccentChroma(value: unknown, fallback: number): number {
  const chroma = finiteNumber(value, fallback);
  return Math.min(MAX_SECTION_ACCENT_CHROMA, Math.max(MIN_SECTION_ACCENT_CHROMA, chroma));
}

export function clampSectionAccentLightness(value: unknown, fallback: number): number {
  const lightness = finiteNumber(value, fallback);
  return Math.min(MAX_SECTION_ACCENT_LIGHTNESS, Math.max(MIN_SECTION_ACCENT_LIGHTNESS, lightness));
}

/**
 * Parse persisted or untrusted data into the full role map. Unknown keys
 * are dropped; absent or malformed roles fall back to the Founders default
 * for that role — the map is always total.
 */
export function parseSectionAccents(value: unknown): SectionAccentMap {
  const source = record(value);
  const parsed = {} as Record<SectionAccentRole, SectionAccent>;
  for (const role of SECTION_ACCENT_ROLES) {
    const fallback = FOUNDERS_SECTION_ACCENTS[role];
    const entry = record(source[role]);
    parsed[role] = Object.freeze({
      hue: wrapSectionAccentHue(entry.hue, fallback.hue),
      chroma: clampSectionAccentChroma(entry.chroma, fallback.chroma),
      lightness: clampSectionAccentLightness(entry.lightness, fallback.lightness),
    });
  }
  return Object.freeze(parsed);
}

const kebab = (role: string): string => role.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

export function sectionAccentCssVariable(role: SectionAccentRole): string {
  return `--ncl-section-${kebab(role)}`;
}

export interface ResolvedSectionAccents {
  readonly roles: SectionAccentMap;
  /** `--ncl-section-<kebab-role>` → `oklch(...)` for every role. */
  readonly cssVariables: Readonly<Record<string, string>>;
}

/** Deterministically project a role map to its CSS custom properties. */
export function resolveSectionAccents(value?: unknown): ResolvedSectionAccents {
  const roles = value === undefined ? FOUNDERS_SECTION_ACCENTS : parseSectionAccents(value);
  const cssVariables: Record<string, string> = {};
  for (const role of SECTION_ACCENT_ROLES) {
    const { hue, chroma, lightness } = roles[role];
    cssVariables[sectionAccentCssVariable(role)] = oklch(lightness, chroma, hue);
  }
  return Object.freeze({ roles, cssVariables: Object.freeze(cssVariables) });
}

/**
 * Tone-down: collapse every role to its group lead's color. Pure and
 * idempotent — the input map is never mutated, and collapsing a collapsed
 * map returns the same values.
 */
export function collapseSectionAccents(accents: SectionAccentMap): SectionAccentMap {
  const collapsed = {} as Record<SectionAccentRole, SectionAccent>;
  for (const role of SECTION_ACCENT_ROLES) {
    const lead = SECTION_ACCENT_GROUP_LEAD[SECTION_ACCENT_GROUP_OF[role]];
    const { hue, chroma, lightness } = accents[lead];
    collapsed[role] = Object.freeze({ hue, chroma, lightness });
  }
  return Object.freeze(collapsed);
}
